import type { ChangeEvent } from "react";

import { Check, Pencil, X } from "lucide-react";

import formatComma from "react-lib/utils/formatComma";

import { ButtonV4, ChipV4, FieldV4, SelectV4 } from "../common/ui";
import type { SetEntry } from "../common/sequence/OrderSheetV4";
import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  SEARCH_MAX_RESULTS,
  SEARCH_MIN_QUERY,
  SHEET_CATEGORIES,
  formatPrice,
  type PickItem,
  type SheetCategory,
} from "./ordersModelV4";
import { advanceId, advanceTotal, type AdvanceOrder } from "./orderAppointmentModelV4";
import type { AdvanceSearch } from "./CardOrderAppointmentV4";

// Types
export type AdvancePanel = "" | "sets" | "favs";

type OrderAppointmentAdvanceV4Props = {
  // data
  orders: AdvanceOrder[];
  search: AdvanceSearch;
  readOnly: boolean;
  panel: AdvancePanel;
  editingKey: string;
  // callback
  onAddPick: (pick: PickItem) => void;
  onAddOrders: (entries: SetEntry[]) => void;
  onEditOrder: (key: string, data: Partial<AdvanceOrder>) => void;
  onRemoveOrder: (key: string) => void;
  onTogglePanel: (panel: "sets" | "favs") => void;
  onChangeEditing: (key: string) => void;
};

// Const
const CATEGORY_OPTIONS = [
  { value: "all", text: "ทุกหมวด" },
  ...SHEET_CATEGORIES.map((category) => ({ value: category, text: CATEGORY_LABELS[category] })),
];

/** คำสั่งล่วงหน้าของนัด: ค้นหา / Order Set / รายการโปรด + ตารางที่เพิ่มแล้ว */
const OrderAppointmentAdvanceV4 = (props: OrderAppointmentAdvanceV4Props) => {
  const { orders, search, readOnly, panel } = props;

  const addedKeys = new Set(orders.map((order) => order.key));
  const hasQuery = search.query.trim().length >= SEARCH_MIN_QUERY;

  // Handler
  const handleChangeQuery = (e: ChangeEvent<HTMLInputElement>) => {
    search.onChangeQuery(e.target.value);
  };

  const handleChangeCategory = (value: string) => {
    search.onChangeCategory(value as SheetCategory | "all");
  };

  const handleToggleSets = () => {
    props.onTogglePanel("sets");
  };

  const handleToggleFavs = () => {
    props.onTogglePanel("favs");
  };

  // Render
  const renderPick = (pick: PickItem) => (
    <div key={pick.key} className="hv4-ord-result">
      <div className="hv4-ord-result-btn">
        <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
          {CATEGORY_LABELS[pick.category]}
        </ChipV4>
        <b className="hv4-ord-result-name">{pick.name}</b>
        <span className="hv4-ord-result-price">{formatPrice(pick.price)}</span>
        <button
          type="button"
          className="hv4-ord-ap-add"
          disabled={addedKeys.has(pick.key) || readOnly}
          onClick={() => props.onAddPick(pick)}
        >
          {addedKeys.has(pick.key) ? "✓ เพิ่มแล้ว" : "+ เพิ่ม"}
        </button>
      </div>
    </div>
  );

  const renderSearch = () => (
    <div className="hv4-ord-search-row">
      <FieldV4
        className="hv4-ord-search-query"
        label="ค้นหารายการสั่งล่วงหน้า"
        htmlFor="hv4_ord_ap_q"
      >
        <input
          id="hv4_ord_ap_q"
          className="hv4-input"
          placeholder="พิมพ์ชื่อแลป ยา หัตถการ ฯลฯ"
          value={search.query}
          onChange={handleChangeQuery}
        />
      </FieldV4>
      <FieldV4 className="hv4-ord-search-cat" label="หมวด" htmlFor="hv4_ord_ap_c">
        <SelectV4
          id="hv4_ord_ap_c"
          options={CATEGORY_OPTIONS}
          value={search.category}
          onChange={handleChangeCategory}
        />
      </FieldV4>
      <ButtonV4
        variant={panel === "sets" ? "primary" : "ghost"}
        aria-expanded={panel === "sets"}
        onClick={handleToggleSets}
      >
        Order Set <b>{search.orderSets.length}</b>
      </ButtonV4>
      <ButtonV4
        variant={panel === "favs" ? "primary" : "ghost"}
        aria-expanded={panel === "favs"}
        onClick={handleToggleFavs}
      >
        ★ รายการโปรด <b>{search.favourites.length}</b>
      </ButtonV4>
    </div>
  );

  const renderSets = () => (
    <div className="hv4-ord-ap-box" aria-label="Order Set">
      {search.orderSets.map((set) => {
        const entries = search.setEntriesOf(set.id);
        const left = entries.filter((entry) => !addedKeys.has(entry.key));

        return (
          <div key={set.id} className="hv4-ord-ap-boxrow">
            <b>{set.name}</b>
            <span className="hv4-ord-ap-hint">{entries.length} รายการ</span>
            <button
              type="button"
              className="hv4-ord-ap-add"
              disabled={left.length === 0}
              onClick={() => props.onAddOrders(left)}
            >
              + เพิ่มทั้งชุด ({left.length})
            </button>
          </div>
        );
      })}
      {search.orderSets.length === 0 && <div className="hv4-ord-ap-empty">ยังไม่มี Order Set</div>}
    </div>
  );

  const renderFavs = () => (
    <div className="hv4-ord-ap-box" aria-label="รายการโปรด">
      {search.favourites.map(renderPick)}
      {search.favourites.length === 0 && <div className="hv4-ord-ap-empty">ยังไม่มีรายการโปรด</div>}
    </div>
  );

  const renderRow = (order: AdvanceOrder, index: number) => {
    const isEditing = props.editingKey === order.key;

    return (
      <tr key={order.key}>
        <td>{advanceId(index)}</td>
        <td>
          <ChipV4 size="sm" tone={CATEGORY_TONES[order.kind]}>
            {CATEGORY_LABELS[order.kind]}
          </ChipV4>
        </td>
        <td>
          <b>{order.name}</b>
          {isEditing ? (
            <input
              className="hv4-input"
              aria-label={`หมายเหตุ ${order.name}`}
              placeholder="หมายเหตุ เช่น งดอาหาร 8 ชม."
              value={order.note}
              onChange={(e) => props.onEditOrder(order.key, { note: e.target.value })}
            />
          ) : (
            !!order.note && <div className="hv4-ord-ap-hint">{order.note}</div>
          )}
        </td>
        <td className="hv4-ord-ap-num-col">
          {isEditing ? (
            <input
              className="hv4-input"
              type="number"
              min={0}
              aria-label={`ราคา ${order.name}`}
              value={order.price}
              onChange={(e) => props.onEditOrder(order.key, { price: Number(e.target.value) })}
            />
          ) : (
            <b>{formatPrice(order.price)}</b>
          )}
        </td>
        <td>
          <button
            type="button"
            className="hv4-ord-ap-del"
            aria-label={`ลบ ${order.name}`}
            disabled={readOnly}
            onClick={() => props.onRemoveOrder(order.key)}
          >
            <X size={14} />
          </button>
        </td>
        <td>
          <button
            type="button"
            className="hv4-ord-ap-edit"
            aria-label={`${isEditing ? "เสร็จ" : "แก้ไข"} ${order.name}`}
            disabled={readOnly}
            onClick={() => props.onChangeEditing(isEditing ? "" : order.key)}
          >
            {isEditing ? <Check size={14} /> : <Pencil size={14} />}
          </button>
        </td>
      </tr>
    );
  };

  const renderTable = () => (
    <table className="hv4-ord-ap-table hv4-ord-ap-orders">
      <thead>
        <tr>
          <th>ID</th>
          <th>Type</th>
          <th>Details</th>
          <th className="hv4-ord-ap-num-col">ราคา</th>
          <th>Del</th>
          <th>Edit</th>
        </tr>
      </thead>
      <tbody>
        {orders.map(renderRow)}
        {orders.length === 0 && (
          <tr>
            <td colSpan={6} className="hv4-ord-ap-empty">
              ยังไม่มีคำสั่งล่วงหน้า · ค้นหาหรือเลือกจาก Order Set / รายการโปรด
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="hv4-ord-ap-advance">
      <div>
        <b className="hv4-ord-ap-sub">สั่งออเดอร์ล่วงหน้า</b>
        <span className="hv4-ord-ap-hint">
          {" "}
          · สั่งไว้ให้นัดครั้งถัดไป ระบบจะดึงเข้าใบคำสั่งเมื่อเปิด Encounter วันนัด
        </span>
      </div>

      {!readOnly && renderSearch()}

      {hasQuery && (
        <div className="hv4-ord-results">
          {search.results.slice(0, SEARCH_MAX_RESULTS).map(renderPick)}
          {search.results.length === 0 && !search.isSearching && (
            <div className="hv4-ord-results-empty">ไม่พบรายการ ลองเปลี่ยนคำค้นหรือหมวด</div>
          )}
        </div>
      )}

      {panel === "sets" && renderSets()}

      {panel === "favs" && renderFavs()}

      {renderTable()}
      {orders.length > 0 && (
        <div className="hv4-ord-ap-total">
          {orders.length} รายการ · รวม {formatComma(advanceTotal(orders))} บาท (ราคาประมาณการ)
        </div>
      )}
    </div>
  );
};

OrderAppointmentAdvanceV4.displayName = "OrderAppointmentAdvanceV4";

export default OrderAppointmentAdvanceV4;
